// CustomListingsPage.tsx (with skeleton components)
import { useState, type ReactNode, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Building2,
  MapPin,
  Filter,
  Grid3x3,
  List,
  Map,
  X,
  Sparkles,
  Clock,
  Star,
  Heart,
  Share2,
  ChevronDown,
  Loader2,
  Search,
  RefreshCw,
} from "lucide-react";
import { MyInfiniteLoadMore } from "../components/MyInfiniteLoadMore";
import { myGetList } from "../hooks/myGetList";
import MySearchBar from "./MySearchBar";
import MapView, { MapItem } from "./MapView";

// Skeleton Components
export const SkeletonBox = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 ${className}`} />
);

export const SkeletonText = ({ lines = 3, className = "" }: { lines?: number; className?: string }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBox
        key={i}
        className={`h-4 ${i === lines - 1 ? "w-1/2" : "w-full"}`}
      />
    ))}
  </div>
);

export const SkeletonProductCard = () => (
  <div className="space-y-3 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
    <SkeletonBox className="h-48 w-full rounded-lg" />
    <SkeletonText lines={3} />
    <div className="flex justify-between items-center pt-2">
      <SkeletonBox className="h-6 w-20" />
      <SkeletonBox className="h-8 w-8 rounded-full" />
    </div>
  </div>
);

export const SkeletonProductGrid = ({ count = 8 }: { count?: number }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonProductCard key={i} />
    ))}
  </div>
);

export const SkeletonProductList = ({ count = 5 }: { count?: number }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
        <SkeletonBox className="h-24 w-24 rounded-lg flex-shrink-0" />
        <div className="flex-1">
          <SkeletonText lines={3} />
        </div>
      </div>
    ))}
  </div>
);

export const SkeletonMapView = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border overflow-hidden">
    <div className="h-[500px] bg-gray-100 dark:bg-gray-700 relative">
      {/* Map tiles skeleton */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading map data...</p>
        </div>
      </div>
      {/* Grid overlay for map loading effect */}
      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-px bg-gray-200 dark:bg-gray-600 opacity-20">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="bg-gray-300 dark:bg-gray-500" />
        ))}
      </div>
    </div>
  </div>
);

export const SkeletonCategories = ({ count = 6 }: { count?: number }) => (
  <div className="flex flex-wrap gap-2 mb-6">
    <SkeletonBox className="h-10 w-16 rounded-full" />
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonBox key={i} className="h-10 w-24 rounded-full" />
    ))}
  </div>
);

export const SkeletonFilters = () => (
  <div className="space-y-4">
    <div className="flex gap-3">
      <SkeletonBox className="h-10 w-32 rounded-xl" />
      <SkeletonBox className="h-10 w-32 rounded-xl" />
      <SkeletonBox className="h-10 w-32 rounded-xl" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <SkeletonBox className="h-4 w-20" />
          <SkeletonBox className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonStats = ({ count = 4 }: { count?: number }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <SkeletonBox className="h-4 w-16" />
            <SkeletonBox className="h-8 w-20" />
          </div>
          <SkeletonBox className="h-10 w-10 rounded-xl" />
        </div>
      </div>
    ))}
  </div>
);

export const SkeletonHero = () => (
  <div className="relative bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 h-64">
    <div className="absolute inset-0 bg-black/20" />
    <div className="relative max-w-7xl mx-auto px-4 py-16 text-center">
      <SkeletonBox className="h-8 w-32 mx-auto mb-4 rounded-full" />
      <SkeletonBox className="h-12 w-64 mx-auto mb-4" />
      <SkeletonBox className="h-6 w-96 mx-auto" />
    </div>
  </div>
);

// Types
export interface StatItem {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  [key: string]: FilterOption[];
}

export interface CategoryItem {
  id: string;
  label: string;
  icon?: React.ElementType;
}

export type ViewMode = "grid" | "list" | "map";

export interface CustomListingsPageProps<T> {
  // Core configuration
  endpoint: string;
  method?: string;
  queryKey: string;

  // Header configuration
  headerText?: string;
  headerSubtext?: string;
  headerIcon?: React.ElementType;
  headerButtons?: React.ReactNode;
  headerBadge?: string;
  showHeader?: boolean;
  heroStats?: StatItem[];

  // Search configuration
  searchPlaceholder?: string;
  enableSearch?: boolean;
  searchDebounceMs?: number;

  // View configuration
  defaultView?: ViewMode;
  gridCols?: {
    mobile?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };

  // Filter configuration
  filterConfig?: FilterConfig;
  enableFilters?: boolean;

  // Categories configuration
  categoryKey?: string;
  categories?: CategoryItem[];
  categoriesLabel?: string;
  enableCategories?: boolean;

  // Map configuration
  enableMapView?: boolean;
  mapItems?: MapItem[];
  mapCenter?: { lat: number; lng: number };
  mapZoom?: number;

  // Sidebar configuration
  sidebar?: ReactNode;

  // Banner configuration
  banner?: ReactNode;

  // Custom header (overrides default header)
  customHeader?: ReactNode;

  // Sticky search configuration
  stickySearchBlock?: ReactNode;
  navbarHeight?: number;

  // Render functions
  renderItem: (item: T, view: ViewMode) => ReactNode;
  renderEmptyState?: () => ReactNode;
  renderLoadingState?: () => ReactNode;
  renderMapView?: (listings: T[], onListingClick: (item: T) => void) => ReactNode;

  // Event handlers
  onFilterChange?: (filters: Record<string, any>) => void;
  onSearch?: (searchTerm: string) => void;
  onCategoryChange?: (categoryId: string) => void;
  onItemClick?: (item: T) => void;
  onLike?: (item: T) => void;

  // Additional query parameters
  additionalParams?: Record<string, any>;

  // Styling options
  containerClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  filterBarClassName?: string;

  // Sort options
  sortOptions?: { value: string; label: string }[];
  defaultSort?: string;
}

export default function CustomListingsPage<T extends {
  id: string;
  latitude?: number;
  longitude?: number;
  [key: string]: any;
}>({
  // Core
  endpoint,
  method = "GET",
  queryKey,

  // Header
  headerText,
  headerSubtext,
  headerIcon: HeaderIcon,
  headerButtons,
  headerBadge,
  showHeader = true,
  heroStats = [],

  // Search
  searchPlaceholder = "Search...",
  enableSearch = true,
  searchDebounceMs = 500,

  // View
  defaultView = "grid",
  gridCols = { mobile: 1, sm: 2, md: 3, lg: 4, xl: 4 },

  // Filters
  filterConfig = {},
  enableFilters = true,

  // Categories
  categoryKey = "category",
  categories = [],
  categoriesLabel = "Categories",
  enableCategories = true,

  // Map
  enableMapView = false,
  mapItems,
  mapCenter = { lat: 4.175278, lng: 73.508889 },
  mapZoom = 13,

  // Sidebar/Banner
  sidebar,
  banner,

  // Custom
  customHeader,

  // Sticky search
  stickySearchBlock,
  navbarHeight = 64,

  // Render functions
  renderItem,
  renderEmptyState,
  renderLoadingState,
  renderMapView: customRenderMapView,

  // Events
  onFilterChange,
  onSearch,
  onCategoryChange,
  onItemClick,
  onLike,

  // Additional params
  additionalParams = {},

  // Styling
  containerClassName = "",
  contentClassName = "",
  headerClassName = "",
  filterBarClassName = "",

  // Sort
  sortOptions = [
    { value: "newest", label: "Newest first" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
  ],
  defaultSort = "newest",
}: CustomListingsPageProps<T>) {
  // State
  const [view, setView] = useState<ViewMode>(defaultView);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    Object.keys(filterConfig).forEach(key => {
      const options = filterConfig[key];
      if (options && options[0]) {
        initial[key] = options[0].value;
      }
    });
    return initial;
  });
  const [sortBy, setSortBy] = useState(defaultSort);

  // Refs
  const mainContentRef = useRef<HTMLDivElement>(null);
  const searchDebounceRef = useRef<number>();
  const isInitialMount = useRef(true);

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(() => {
      setSearchTerm(searchInput);
      onSearch?.(searchInput);
    }, searchDebounceMs);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput, onSearch, searchDebounceMs]);

  // Scroll to top when filters change (only for grid/list views)
  useEffect(() => {
    if (view !== "map" && mainContentRef.current && !isInitialMount.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    isInitialMount.current = false;
  }, [searchTerm, selectedCategory, filterValues, sortBy, view]);

  // Build query parameters
  const queryParams = useMemo(() => ({
    ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),

    ...(selectedCategory && categoryKey
      ? { [categoryKey]: selectedCategory }
      : {}),

    ...Object.entries(filterValues).reduce((acc, [key, value]) => {
      const options = filterConfig[key];
      if (options && options[0] && value !== options[0].value) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>),

    sort: sortBy,
    ...additionalParams,
  }), [
    searchTerm,
    selectedCategory,
    categoryKey,
    filterValues,
    sortBy,
    additionalParams,
    filterConfig
  ]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = myGetList({
    endpoint,
    method,
    key: [queryKey, searchTerm, selectedCategory, filterValues, sortBy, additionalParams],
    query: queryParams,
  });

  const items: T[] = data?.pages.flatMap((p) => p.data) ?? [];
  const isSearching = isFetching && !isLoading && searchTerm !== "";
  const isRefreshing = isFetching && !isLoading && !isFetchingNextPage;

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    return Object.entries(filterValues).filter(([key, value]) => {
      const options = filterConfig[key];
      return options && options[0] && value !== options[0].value;
    }).length;
  }, [filterValues, filterConfig]);

  // Handlers
  const handleSearch = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  const handleCategoryChange = useCallback((value: string) => {
    setSelectedCategory(value);
    onCategoryChange?.(value);
  }, [onCategoryChange]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
    onFilterChange?.({ ...filterValues, [key]: value });
  }, [filterValues, onFilterChange]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setSelectedCategory("");

    const resetValues: Record<string, string> = {};
    Object.keys(filterConfig).forEach(key => {
      const options = filterConfig[key];
      if (options && options[0]) {
        resetValues[key] = options[0].value;
      }
    });
    setFilterValues(resetValues);
    onFilterChange?.(resetValues);
    onSearch?.("");
  }, [filterConfig, onFilterChange, onSearch]);

  const handleItemClick = useCallback((item: T) => {
    onItemClick?.(item);
  }, [onItemClick]);

  const handleLike = useCallback((item: T) => {
    onLike?.(item);
  }, [onLike]);

  const toggleView = useCallback((newView: ViewMode) => {
    setView(newView);
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Refreshing indicator overlay
  const RefreshingIndicator = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed top-20 right-4 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg px-4 py-2 flex items-center gap-2 border border-gray-200 dark:border-gray-700"
    >
      <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
      <span className="text-sm text-gray-600 dark:text-gray-300">Updating...</span>
    </motion.div>
  );

  // Default header component
  const DefaultHeader = useCallback(() => (
    <div className={`relative bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 dark:from-purple-900 dark:via-purple-800 dark:to-pink-900 ${headerClassName}`}>
      <div className="absolute inset-0 bg-black/20 dark:bg-black/40" />
      <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="text-center">
          {headerBadge && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm mb-4">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-sm font-medium text-white">{headerBadge}</span>
            </div>
          )}
          {HeaderIcon && (
            <div className="flex justify-center mb-4">
              <HeaderIcon className="w-12 h-12 text-white" />
            </div>
          )}
          {headerText && (
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {headerText}
            </h1>
          )}
          {headerSubtext && (
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
              {headerSubtext}
            </p>
          )}
          <div className="flex flex-wrap gap-4 justify-center">
            {headerButtons && (
              <div className="flex gap-3">
                {headerButtons}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent" />
    </div>
  ), [headerBadge, HeaderIcon, headerText, headerSubtext, headerClassName]);

  // Hero stats bar
  const HeroStatsBar = useCallback(() => {
    if (heroStats.length === 0) return null;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {heroStats.map((stat, idx) => (
          <div
            key={idx}
            className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-100 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }, [heroStats]);

  // Filter bar component
  const FilterBar = useCallback(() => (
    <div className={`sticky top-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm ${filterBarClassName}`}>
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          {/* Action Buttons */}
          <div className="flex gap-2 flex-1">
            {enableSearch && (
              <div className="flex-1 max-w-md relative">
                <MySearchBar
                  placeholder={searchPlaceholder}
                  value={searchInput}
                  onSearch={handleSearch}
                  loading={isSearching}
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              {enableFilters && Object.keys(filterConfig).length > 0 && (
                <button
                  onClick={toggleFilters}
                  className={`px-4 py-2 rounded-xl border transition-all flex items-center gap-2 ${showFilters
                    ? "bg-purple-500 border-purple-500 text-white"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                  )}
                </button>
              )}

              <div className="flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
                <button
                  onClick={() => toggleView("grid")}
                  className={`p-2 rounded-lg transition-all ${view === "grid"
                    ? "bg-purple-500 text-white"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  aria-label="Grid view"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleView("list")}
                  className={`p-2 rounded-lg transition-all ${view === "list"
                    ? "bg-purple-500 text-white"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </button>
                {enableMapView && (
                  <button
                    onClick={() => toggleView("map")}
                    className={`p-2 rounded-lg transition-all ${view === "map"
                      ? "bg-purple-500 text-white"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    aria-label="Map view"
                  >
                    <Map className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Refresh button */}
              <button
                onClick={handleRetry}
                disabled={isRefreshing}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
                aria-label="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mt-4"
            >
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {Object.entries(filterConfig).map(([key, options]) => (
                    <div key={key} className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <select
                        value={filterValues[key]}
                        onChange={(e) => handleFilterChange(key, e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        {options.map((option: FilterOption) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleClearFilters}
                    className="text-sm text-purple-500 hover:text-purple-600 flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Clear all filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  ), [enableSearch, searchPlaceholder, searchInput, isSearching, handleSearch, enableFilters, filterConfig, showFilters, toggleFilters, activeFiltersCount, view, toggleView, enableMapView, filterValues, handleFilterChange, handleClearFilters, filterBarClassName, handleRetry, isRefreshing]);

  // Categories component
  const CategoriesComponent = useCallback(() => {
    if (!enableCategories || categories.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => handleCategoryChange("")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === ""
            ? "bg-purple-500 text-white shadow-md"
            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
        >
          All
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${selectedCategory === category.id
              ? "bg-purple-500 text-white shadow-md"
              : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
          >
            {category.icon && <category.icon className="w-4 h-4" />}
            {category.label}
          </button>
        ))}
      </div>
    );
  }, [enableCategories, categories, selectedCategory, handleCategoryChange]);

  // Results header
  const ResultsHeader = useCallback(() => (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div className="flex items-center gap-2">
        <span className="text-gray-900 dark:text-white font-semibold">
          {items.length} results found
        </span>
        {searchTerm && (
          <span className="text-sm text-gray-500">
            for "{searchTerm}"
          </span>
        )}
        {activeFiltersCount > 0 && (
          <span className="text-sm text-gray-500">
            with {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''}
          </span>
        )}
        {isSearching && (
          <span className="text-xs text-purple-500 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            searching...
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">Sort by</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {sortOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  ), [items.length, searchTerm, activeFiltersCount, sortBy, sortOptions, isSearching]);

  // Error state
  const ErrorState = useCallback(() => (
    <div className="text-center py-16">
      <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
        <X className="w-12 h-12 text-red-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Something went wrong
      </h3>
      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
        {error instanceof Error ? error.message : "Failed to load listings. Please try again."}
      </p>
      <button
        onClick={handleRetry}
        className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
      >
        <RefreshCw className="w-4 h-4" />
        Try Again
      </button>
    </div>
  ), [error, handleRetry]);

  // Default empty state
  const DefaultEmptyState = useCallback(() => (
    <div className="text-center py-16">
      <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <Home className="w-12 h-12 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        No items found
      </h3>
      <p className="text-gray-500 dark:text-gray-400">
        Try adjusting your search or filters to find what you're looking for
      </p>
      {(searchTerm || activeFiltersCount > 0) && (
        <button
          onClick={handleClearFilters}
          className="mt-4 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
        >
          Clear all filters
        </button>
      )}
    </div>
  ), [searchTerm, activeFiltersCount, handleClearFilters]);

  // Default loading state using skeleton components
  const DefaultLoadingState = useCallback(() => {
    if (view === "list") {
      return <SkeletonProductList count={5} />;
    }
    return <SkeletonProductGrid count={8} />;
  }, [view]);

  // DefaultMapView component with skeleton
  const DefaultMapView = useCallback(() => {
    // Filter items that have valid coordinates
    const listingsWithCoords = items.filter(item =>
      item.latitude && item.longitude
    );

    if (listingsWithCoords.length === 0) {
      return (
        <div className="flex items-center justify-center h-[500px]">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              No properties with location data available
            </p>
          </div>
        </div>
      );
    }

    // Transform items to match MapView's MapItem type
    const mapItems: MapItem[] = listingsWithCoords.map(item => ({
      id: item.id || item.product_id,
      title: item.title || 'Property',
      lat: Number(item.latitude),
      lng: Number(item.longitude),
      image: item.image_url || undefined,
      description: `${item.price ? `MVR ${item.price}` : ''}${item.price_unit ? `/${item.price_unit}` : ''}`,
      extra: {
        ...item,
        price: item.price,
        price_unit: item.price_unit,
      }
    }));

    return (
      <MapView
        items={mapItems}
        height="calc(100vh - 380px)"
        zoom={mapZoom}
        center={[mapCenter.lat, mapCenter.lng]}
        renderPopup={(mapItem: MapItem) => {
          const listing = mapItem.extra;
          return (
            <div className="space-y-3 text-center min-w-[220px] p-2">
              {mapItem.image && (
                <img
                  src={mapItem.image}
                  alt={mapItem.title}
                  className="w-full h-32 object-cover rounded-lg"
                />
              )}
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                {mapItem.title}
              </h3>
              {listing?.price && (
                <p className="text-purple-600 font-bold text-lg">
                  MVR {listing.price}/{listing.price_unit || 'month'}
                </p>
              )}
              {listing?.bedrooms && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {listing.bedrooms} bed{listing.bedrooms !== 1 ? 's' : ''} • {listing.bathrooms} bath{listing.bathrooms !== 1 ? 's' : ''}
                </p>
              )}
              <button
                onClick={() => handleItemClick(listing)}
                className="mt-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm transition-colors w-full font-medium"
              >
                View Details
              </button>
            </div>
          );
        }}
      />
    );
  }, [items, handleItemClick, mapCenter, mapZoom]);

  // Results component with enhanced loading states
  const ResultsComponent = useCallback(() => {
    // Show loading skeleton on initial load
    if (isLoading) {
      if (view === "map" && enableMapView) {
        return <SkeletonMapView />;
      }
      return renderLoadingState ? renderLoadingState() : <DefaultLoadingState />;
    }

    // Show error state
    if (isError) {
      return <ErrorState />;
    }

    // Show empty state
    if (items.length === 0) {
      return renderEmptyState ? renderEmptyState() : <DefaultEmptyState />;
    }

    // Map view
    if (view === "map" && enableMapView) {
      if (customRenderMapView) {
        return customRenderMapView(items, handleItemClick);
      }
      return <DefaultMapView />;
    }

    // Grid or List view
    return (
      <>
        <div
          className={
            view === "grid"
              ? `grid ${getGridColsClass(gridCols)} gap-4 md:gap-6`
              : "flex flex-col gap-4"
          }
        >
          {items.map((item, idx) => (
            <motion.div
              key={item.id || idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.05, 0.5) }}
              className="w-full"
            >
              {renderItem(item, view)}
            </motion.div>
          ))}
        </div>

        <MyInfiniteLoadMore
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      </>
    );
  }, [isLoading, isError, items, view, enableMapView, gridCols, renderItem, hasNextPage, isFetchingNextPage, fetchNextPage, renderLoadingState, renderEmptyState, DefaultLoadingState, ErrorState, DefaultEmptyState, customRenderMapView, handleItemClick, DefaultMapView]);

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${containerClassName}`}>
      {/* Refreshing indicator */}
      <AnimatePresence>
        {isRefreshing && !isLoading && <RefreshingIndicator />}
      </AnimatePresence>

      {/* Header */}
      {showHeader && (customHeader || (headerText && <DefaultHeader />))}

      {/* Hero Stats with skeleton */}
      {heroStats.length > 0 ? (
        <div className="max-w-7xl mx-auto px-4 -mt-8 relative z-10">
          <HeroStatsBar />
        </div>
      ) : isLoading && showHeader ? (
        <div className="max-w-7xl mx-auto px-4 -mt-8 relative z-10">
          <SkeletonStats count={4} />
        </div>
      ) : null}

      {/* Filter Bar */}
      <FilterBar />

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 py-8 ${contentClassName}`}>
        {/* Banner */}
        {banner && <div className="mb-6">{banner}</div>}

        {/* Categories with skeleton */}
        {isLoading && enableCategories && categories.length === 0 ? (
          <SkeletonCategories count={6} />
        ) : (
          <CategoriesComponent />
        )}

        {/* Results Header */}
        <ResultsHeader />

        {/* Conditional Rendering based on view */}
        <AnimatePresence mode="wait">
          <motion.div
            key={view}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex gap-6">
              {sidebar && view !== "map" && (
                <aside className="hidden lg:block w-64 flex-shrink-0">
                  {sidebar}
                </aside>
              )}
              <div className={`flex-1 ${view === "map" ? "h-full" : ""}`} ref={view !== "map" ? mainContentRef : undefined}>
                <ResultsComponent />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Helper function for grid classes
const gridColsMap: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};

function getGridColsClass(gridCols?: {
  mobile?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}) {
  const { mobile = 2, sm, md, lg, xl } = gridCols || {};

  const classes = [gridColsMap[mobile]];
  if (sm) classes.push(`sm:${gridColsMap[sm]}`);
  if (md) classes.push(`md:${gridColsMap[md]}`);
  if (lg) classes.push(`lg:${gridColsMap[lg]}`);
  if (xl) classes.push(`xl:${gridColsMap[xl]}`);

  return classes.filter(Boolean).join(" ");
}